<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, ref, shallowRef } from 'vue'
import { useHead } from '@unhead/vue'
import AuthBrandHeader from '@/components/auth/AuthBrandHeader.vue'
import PlayAgentGame from '@/components/voice-agent/PlayAgentGame.vue'
import CandidateFinished from '@/views/recruiting/CandidateFinished.vue'
import type { LaunchApiClient } from '@/external-launch/launchApi'
import { awaitAttemptOutcome, createLaunchApiClient, provideLaunchApi, readLaunchPlayState } from '@/external-launch/launchApi'
import { createLaunchEventBus } from '@/external-launch/launchEcho'
import { provideLaunchEventBus } from '@/external-launch/launchEventBus'
import type { LaunchBridge } from '@/external-launch/launchBridge'
import { createLaunchBridge } from '@/external-launch/launchBridge'
import type { SessionExitDetails } from '@/components/voice-session/flow'
import type { GameExitReason } from '@/types/gameExit'
import type { LaunchScreenVersion } from '@/types/voiceSession'

// `name` задан явно по той же причине, что и на странице кандидата: автогенератор имён
// (`getRouteName` в `vite.config.ts`) режет PascalCase по границе «строчная→заглавная», а у
// `XCode` такой границы нет — имя схлопнулось бы в `xcode`.
definePage({
  name: 'x-code',
  meta: {
    public: true,
    layout: 'blank',
  },
})

/**
 * Загрузочная страница внешнего запуска.
 *
 * Переход сюда не стоит ничего: по ссылке ходят предпросмотрщики, антивирусы и корпоративные
 * прокси. Код обменивается ТОЛЬКО по кнопке «Начать» — отдельным POST на тот же адрес.
 *
 * Токен запуска живёт исключительно в памяти вкладки: ни localStorage, ни sessionStorage, ни
 * cookie. Cookie нашего домена вообще не участвуют — в том же браузере может быть открыта
 * сессия коллеги, и любой запрос страницы обязан идти от имени ученика, а не от её имени.
 * Отсюда же `credentials: 'omit'` на самом обмене.
 *
 * Из приложения на этой странице нет ничего: пустой layout, никакой навигации, а по завершении
 * игры — экран завершения вместо карточки игры с результатами.
 *
 * Версий экрана две (флаг организации `NEW_SESSION_SCREEN`), и решают их два разных ответа:
 *  - вид вступления — чтение `GET x/{code}/screen-version` при загрузке. Код оно не расходует;
 *    до его ответа вступление не показывается, любой отказ или сбой — прежнее вступление;
 *  - всё после обмена — `new_session_screen` из ответа ОБМЕНА: он актуален на момент обмена, а
 *    предварительное чтение могло упасть или устареть (флаг включили между открытием и кнопкой).
 * Новая версия: подготовка → разговор (`SessionFlow`, `entry: 'launch'`) → `SessionFinished`.
 * Прежняя: `PlayAgentGame` сразу после обмена и прежние экраны завершения.
 */

type Screen = 'intro' | 'game' | 'terminal'

/** Вид вступления: `pending` — ответа о версии ещё нет, вступление не показывается. */
type IntroVersion = 'pending' | 'old' | 'new'

/** Вид экранов после обмена. Выбирает ответ обмена, а не предварительное чтение. */
type GameVersion = 'old' | 'new'

/** Исход, который не является отказом запуска: у отказа есть свой машинный код. */
type TerminalKind = 'finished' | 'aborted' | 'token_expired' | 'not_found' | 'rate_limited' | 'generic'

interface ExchangeResponse {
  launch_id: string
  game_id: string
  token: string
  shell_origin: string | null
  new_session_screen?: boolean
}

/**
 * Сколько ждать ответа о версии. Зависший запрос — такой же сбой, как отказ: вступление не
 * должно ждать его вечно, а прежняя версия работает у любой организации.
 */
const SCREEN_VERSION_TIMEOUT_MS = 5000

// Новые экраны — отдельными чанками: они тянут шрифты и стили нового экрана, а прежний поток
// должен остаться без них. Загрузчики вынесены, чтобы страница могла дождаться чанка до показа
// экрана и при сбое загрузки остаться на прежней версии, а не на пустом месте.
const loadSessionIntro = () => import('@/components/voice-session/entry/SessionIntro.vue')
const loadSessionFinished = () => import('@/components/voice-session/entry/SessionFinished.vue')
const loadSessionFlow = () => import('@/components/voice-session/SessionFlow.vue')

const SessionIntro = defineAsyncComponent(loadSessionIntro)
const SessionFinished = defineAsyncComponent(loadSessionFinished)
const SessionFlow = defineAsyncComponent(loadSessionFlow)

const TERMINAL_STYLE: Record<TerminalKind, { icon: string; color: string }> = {
  finished: { icon: 'tabler-circle-check', color: 'success' },
  aborted: { icon: 'tabler-plug-connected-x', color: 'warning' },
  token_expired: { icon: 'tabler-clock-off', color: 'warning' },
  not_found: { icon: 'tabler-link-off', color: 'error' },
  rate_limited: { icon: 'tabler-hourglass', color: 'warning' },
  generic: { icon: 'tabler-alert-triangle', color: 'error' },
}

/** Отказ запуска всегда один по смыслу: материал сейчас не открыть. Разницу несёт текст. */
const REFUSAL_STYLE = { icon: 'tabler-alert-circle', color: 'warning' }

const route = useRoute('x-code')
const { t, te, locale, availableLocales } = useI18n()

const code = String(route.params.code)
const screen = ref<Screen>('intro')
const starting = ref(false)
const gameId = ref('')
const terminalKind = ref<TerminalKind>('generic')
const refusalCode = ref<string | null>(null)
const endNotSent = ref(false)
const introVersion = ref<IntroVersion>('pending')
const gameVersion = ref<GameVersion | null>(null)

/** Экран завершения уже показан, но мы ещё ждём от сервера исход попытки. */
const savingResult = ref(false)

/**
 * В какой версии показывать экран завершения и отказа. После обмена — по ответу обмена; до него
 * (отказ обмена) — в той же версии, что и вступление, чтобы стиль не менялся посреди пути.
 */
const isNewScreen = computed(() => gameVersion.value
  ? gameVersion.value === 'new'
  : introVersion.value === 'new')

/**
 * Клиент поверхности игры. Провайдится ССЫЛКОЙ: `provide()` обязан отработать в `setup`, а
 * токен появляется только после обмена. `PlayAgentGame` монтируется уже после него.
 */
const launchApiClient = shallowRef<LaunchApiClient | null>(null)

provideLaunchApi(launchApiClient)

const launchEventBus = createLaunchEventBus(launchApiClient)

provideLaunchEventBus(launchEventBus)

let bridge: LaunchBridge | null = null

const needsRelaunch = computed(() => refusalCode.value !== null || terminalKind.value !== 'finished')

const terminalView = computed(() => {
  const prefix = refusalCode.value
    ? `externalLaunch.refusal.${refusalCode.value}`
    : `externalLaunch.terminal.${terminalKind.value}`

  const style = refusalCode.value ? REFUSAL_STYLE : TERMINAL_STYLE[terminalKind.value]

  return {
    ...style,
    title: t(`${prefix}.title`),
    text: needsRelaunch.value
      ? `${t(`${prefix}.text`)} ${t('externalLaunch.relaunch_hint')}`
      : t(`${prefix}.text`),
  }
})

const requirements = computed<string[]>(() => [
  t('externalLaunch.intro.requirement_mic'),
  t('externalLaunch.intro.requirement_camera'),
  t('externalLaunch.intro.requirement_browser'),
  t('externalLaunch.intro.requirement_network'),
])

/**
 * Язык страницы.
 *
 * Публичной ручки, которая отдала бы локаль организации, на сегодня нет: `organizations/get-meta`
 * возвращает только оформление, а `availableLanguages` приезжают в cookie вместе с логином,
 * которого у ученика внешнего запуска нет. Поэтому берётся язык этого хоста: в RU-сборке всегда
 * `ru`, иначе — язык из cookie (если этот браузер уже выбирал его) и запасной язык приложения.
 */
function applyLaunchLocale(): void {
  const preferred = import.meta.env.VITE_APP_RU === 'only'
    ? 'ru'
    : String(useCookie<string | null>('language').value ?? '')

  if (preferred && availableLocales.includes(preferred))
    locale.value = preferred
}

applyLaunchLocale()

useHead({ title: t('externalLaunch.document_title') })

/**
 * Признак версии по коду — до обмена, без побочных эффектов: код не расходуется. Как и обмен,
 * без cookie: страница ничего не делает от имени того, кто залогинен в этом браузере. Любой
 * отказ (404 выключенной функции, 429 лимита), нечитаемый ответ, сбой сети и таймаут — `false`,
 * то есть прежняя версия.
 */
async function readScreenVersion(): Promise<boolean> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), SCREEN_VERSION_TIMEOUT_MS)

  try {
    const response = await fetch(`/x/${encodeURIComponent(code)}/screen-version`, {
      credentials: 'omit',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })

    if (!response.ok)
      return false

    const body = await response.json().catch(() => null) as Partial<LaunchScreenVersion> | null

    return body?.new_session_screen === true
  }
  catch {
    return false
  }
  finally {
    clearTimeout(timeout)
  }
}

let newGameScreens: Promise<boolean> | null = null

/**
 * Чанки экранов после обмена новой версии. Загрузка начинается, как только показано новое
 * вступление, чтобы после обмена не ждать; если вступление было прежним (флаг включили после
 * открытия ссылки), — после обмена. `false` — чанк не загрузился.
 */
function loadNewGameScreens(): Promise<boolean> {
  newGameScreens ??= Promise.all([loadSessionFlow(), loadSessionFinished()]).then(() => true, () => false)

  return newGameScreens
}

/**
 * Вид вступления. Новое вступление показывается только вместе с загруженными чанками его и
 * экрана завершения: отказ обмена показывается в той же версии, и пустого места вместо экрана
 * быть не должно. Не загрузились — прежнее вступление.
 */
async function resolveIntroVersion(): Promise<void> {
  const isNew = await readScreenVersion()

  const ready = isNew && await Promise.all([loadSessionIntro(), loadSessionFinished()]).then(() => true, () => false)

  introVersion.value = ready ? 'new' : 'old'

  if (ready)
    loadNewGameScreens()
}

resolveIntroVersion()

function showTerminal(kind: TerminalKind, refusal: string | null = null): void {
  refusalCode.value = refusal
  terminalKind.value = kind
  screen.value = 'terminal'
}

/**
 * Есть ли у кода отказа свой текст. Список кодов нарочно не дублируется в TS: его единственный
 * источник — `Modules\ExternalLaunch\Enums\LaunchRefusalCodeEnum`, а полнота ключей закрыта
 * тестом переводов. Код, которого мы не знаем, показывается общим экраном, а не голым ключом.
 * `en` спрашивается отдельно: `te()` смотрит только текущую локаль, а ключи ядра заводятся в
 * `en` и `ru`, остальные локали приходят следом.
 */
function hasRefusalText(refusal: string): boolean {
  const key = `externalLaunch.refusal.${refusal}.title`

  return te(key) || te(key, 'en')
}

/**
 * Продуктовый отказ приходит 422 с машинным кодом `LaunchRefusalCodeEnum` — текст по нему у нас
 * свой. Неизвестный код публикации, выключённый признак функциональности и чужая организация
 * отвечают одинаковым 404: их ученик видит как «ссылка не работает», и различать их нельзя
 * намеренно.
 */
function showRefusal(status: number, body: any): void {
  const refusal = typeof body?.code === 'string' ? body.code : null

  if (status === 422 && refusal && hasRefusalText(refusal)) {
    showTerminal('generic', refusal)

    return
  }

  if (status === 404) {
    showTerminal('not_found')

    return
  }

  showTerminal(status === 429 ? 'rate_limited' : 'generic')
}

/**
 * Обмен кода. Ни cookie, ни XSRF: страницу открыли из чужой системы обучения, нашей сессии у неё
 * нет и быть не может, а CSRF с `x/*` снят на сервере. Повтор запрещён — код одноразовый, и
 * второй POST того же кода отказал бы; поэтому кнопка блокируется на время запроса.
 */
async function startLaunch(): Promise<void> {
  if (starting.value)
    return

  starting.value = true

  try {
    const response = await fetch(`/x/${encodeURIComponent(code)}`, {
      method: 'POST',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    })

    const body = await response.json().catch(() => null)

    if (!response.ok) {
      showRefusal(response.status, body)

      return
    }

    await openGame(body as ExchangeResponse)
  }
  catch {
    showTerminal('generic')
  }
  finally {
    starting.value = false
  }
}

/**
 * Экран после обмена — по признаку из ответа обмена, а не по предварительному чтению.
 *
 * Клиент запуска кладётся в ссылку ДО показа игры: и `PlayAgentGame`, и `SessionFlow` (через
 * `useDialogManager`) читают его один раз, в своём `setup`, — смонтированные раньше, они ушли бы
 * мимо поверхности запуска.
 *
 * Если чанк нового экрана не загрузился (например, между открытием страницы и кнопкой вышел
 * релиз), ученик играет в прежнем экране: код уже обменян, и тупик стоил бы ему попытки, а
 * поверхность запуска у обеих версий одна.
 */
async function openGame(exchanged: ExchangeResponse): Promise<void> {
  const useNewScreen = exchanged.new_session_screen === true && await loadNewGameScreens()

  launchApiClient.value = createLaunchApiClient({
    token: exchanged.token,
    onUnauthorized: handleAccessLost,
  })

  bridge = createLaunchBridge({
    launchId: exchanged.launch_id,
    shellOrigin: exchanged.shell_origin,
  })

  gameId.value = exchanged.game_id
  gameVersion.value = useNewScreen ? 'new' : 'old'
  screen.value = 'game'
}

/**
 * Токен истёк, запуск отозвали или публикацию выключили — guard поверхности отвечает 401 на
 * любое обращение. Играть дальше нечем: показываем экран и предлагаем запустить материал заново.
 */
function handleAccessLost(): void {
  if (screen.value === 'terminal')
    return

  closeLaunch()
  showTerminal('token_expired')
}

/**
 * Немедленный статус внешней оболочке (design.md, решение 5).
 *
 * Признак «состоялась» берётся ТОЛЬКО у сервера. Своего мнения у страницы нет и быть не может:
 * состоявшейся попытку делает речь игрока (`VoiceSession::hasPlayerParticipation()`), а не то,
 * какой кнопкой человек вышел. Пустая игра, где ученик не сказал ни слова, уезжала бы в систему
 * обучения как состоявшаяся — и уже не откатилась бы: результат там не понижают.
 *
 * **Сервер не ответил за предел — мост молчит.** У сообщения одно поле, `held`, и оба его
 * значения — утверждение о факте, которого мы не знаем. Ложное «состоялась» необратимо, ложное
 * «не состоялась» — запись о попытке, которой не было. Молчание не теряет ничего: у оболочки
 * есть второй путь, чтение состояния по своему праву, а итоговая доставка придёт в любом случае.
 */
async function notifyShell(reason: GameExitReason): Promise<void> {
  // Мост берётся В НАЧАЛЕ: чтение состояния может вернуть 401, а `handleAccessLost` обнулит
  // и мост, и клиент прямо посреди этого `await`.
  const shell = bridge

  if (!shell?.isAvailable)
    return

  // Игра не начиналась вовсе (не стартовала; у прежнего экрана — ещё и открыта в другой вкладке,
  // новый поток с такого экрана не выходит вовсе) — ждать нечего: статус писать некому. Состояние
  // читается один раз, и только чтобы не соврать про попытку, которую в это время ведёт соседняя
  // вкладка.
  if (reason === 'aborted') {
    shell.notifyAttemptFinished((await readLaunchPlayState(launchApiClient.value))?.attempt_held === true)

    return
  }

  savingResult.value = true

  try {
    const held = await awaitAttemptOutcome(() => launchApiClient.value)

    if (held !== null)
      shell.notifyAttemptFinished(held)
  }
  finally {
    savingResult.value = false
  }
}

/** Соединение канала событий не переживает сессию, токен не переживает попытку. */
function closeLaunch(): void {
  launchEventBus.disconnect()
  bridge?.dispose()
  bridge = null
  launchApiClient.value = null
}

/**
 * Экран завершения показывается ДО ожидания сервера, а не после: ожидание длится секунды, и всё
 * это время ученик смотрел бы на застывшую игру. Текст экрана от исхода попытки не зависит —
 * он про то, что разговор окончен, — а пока идёт опрос, под ним видно «сохраняем результат».
 */
async function handleGameExit(reason: GameExitReason): Promise<void> {
  showTerminal(reason === 'aborted' ? 'aborted' : 'finished')

  await notifyShell(reason)

  closeLaunch()
}

/**
 * Выход из нового потока. Отличие от прежнего одно: после 401 поток ещё может досказать свой
 * выход (`aborted` на отказе чтения игры) — экран «доступ истёк» уже показан, клиент и мост
 * закрыты, и перекрывать его «разговор не состоялся» нельзя.
 */
async function handleSessionExit(reason: GameExitReason, details: SessionExitDetails): Promise<void> {
  if (screen.value === 'terminal')
    return

  endNotSent.value = details.endNotSent
  await handleGameExit(reason)
}

onBeforeUnmount(closeLaunch)
</script>

<template>
  <div class="launch-page">
    <!-- Версия вступления ещё не известна: ни одно из двух вступлений не показывается. -->
    <div
      v-if="screen === 'intro' && introVersion === 'pending'"
      class="launch-page__pending"
      role="status"
    >
      <VProgressCircular
        indeterminate
        size="40"
        color="primary"
      />
      <span class="d-sr-only">{{ t('loading') }}</span>
    </div>

    <!--
      Новая версия. Поток монтируется только после обмена: клиент запуска к этому моменту уже в
      ссылке. Уход со страницы разговор не завершает — это свойство самого потока, опции нет.
    -->
    <SessionFlow
      v-else-if="screen === 'game' && gameVersion === 'new' && gameId"
      entry="launch"
      :game-id="gameId"
      :exit-handler="handleSessionExit"
    />

    <SessionIntro
      v-else-if="screen === 'intro' && introVersion === 'new'"
      entry="launch"
      :starting="starting"
      @start="startLaunch"
    />

    <SessionFinished
      v-else-if="screen === 'terminal' && isNewScreen"
      entry="launch"
      :outcome="terminalKind"
      :refusal-code="refusalCode"
      :saving="savingResult"
      :end-not-sent="endNotSent"
    />

    <!--
      Прежняя версия.
      `show-preparation` выключён: `games.get.preparation` не входит в поверхность запуска, диалог
      получил бы 403. `end-session-on-page-exit` выключён по тому же доводу, что и у кандидата:
      F5 иначе финализировал бы сессию, игра стала бы терминальной, и повторный запуск из системы
      обучения не вернул бы ученика в неё — попытка и квота сгорели бы.
    -->
    <PlayAgentGame
      v-else-if="screen === 'game' && gameId"
      :game-id="gameId"
      :exit-handler="handleGameExit"
      :show-preparation="false"
      :end-session-on-page-exit="false"
    />

    <div
      v-else
      class="launch-page__shell"
    >
      <div class="launch-page__brand">
        <AuthBrandHeader />
      </div>

      <VCard
        v-if="screen === 'intro'"
        class="launch-card"
      >
        <VCardText>
          <h1 class="text-h5 mb-2">
            {{ t('externalLaunch.intro.title') }}
          </h1>

          <p class="text-body-1 mb-6">
            {{ t('externalLaunch.intro.lead') }}
          </p>

          <h2 class="text-h6 mb-2">
            {{ t('externalLaunch.intro.requirements_title') }}
          </h2>

          <VList
            density="compact"
            class="launch-page__requirements"
          >
            <VListItem
              v-for="requirement in requirements"
              :key="requirement"
              :title="requirement"
            >
              <template #prepend>
                <VIcon
                  icon="tabler-point-filled"
                  size="14"
                  class="me-2"
                />
              </template>
            </VListItem>
          </VList>
        </VCardText>

        <VCardActions class="launch-page__actions">
          <VSpacer />
          <VBtn
            color="primary"
            variant="elevated"
            size="large"
            :loading="starting"
            :disabled="starting"
            @click="startLaunch"
          >
            {{ t('externalLaunch.intro.start') }}
          </VBtn>
        </VCardActions>
      </VCard>

      <CandidateFinished
        v-else-if="screen === 'terminal'"
        :title="terminalView.title"
        :text="terminalView.text"
        :icon="terminalView.icon"
        :color="terminalView.color"
      />

      <!-- Пока ждём от сервера исход попытки, экран завершения не выглядит застывшим. -->
      <div
        v-if="savingResult"
        class="launch-page__saving"
        role="status"
      >
        <VProgressCircular
          indeterminate
          size="20"
          width="2"
          color="primary"
        />
        <span class="text-body-2">{{ t('externalLaunch.saving') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Ответ о версии обычно приходит за доли секунды — спиннер появляется с задержкой, чтобы не мигать.
.launch-page__pending {
  display: flex;
  align-items: center;
  justify-content: center;
  animation: launch-page-appear 0.2s ease 0.4s both;
  min-block-size: 100vh;
  min-block-size: 100dvh;
}

@keyframes launch-page-appear {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.launch-page__shell {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem 1rem;
  min-block-size: 100vh;
  justify-content: center;
  align-items: center;
}

.launch-page__brand {
  color: rgb(var(--v-global-theme-primary));
}

.launch-card {
  inline-size: 100%;
  max-inline-size: 720px;
}

.launch-page__requirements {
  background: transparent;
}

.launch-page__actions {
  padding-block-end: 1rem;
  padding-inline: 1rem;
}

.launch-page__saving {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  opacity: 0.85;
}
</style>
